/* The dialogs that change an agent's tables: a new table, a table's settings, a template.
   Each plans its change as it is filled in, shows the plan's steps (what it does to the rows,
   why the rows block it), and makes it with that plan. */
import React, { useMemo, useState, type ReactNode } from "react";

import SpacePlanSteps from "@/shared/components/SpacePlanSteps";
import { confirmDangerCls, DangerButton } from "@/shared/components/settings-atoms";
import { BrandButton, GhostButton } from "@/shared/components/ui-kit";
import { inputCls } from "@/shared/components/task-atoms";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { Switch } from "@/shared/components/ui/switch";

import { usePlannedChange } from "./usePlannedChange";

import type { SpaceAccess, SpaceAgentCan, SpaceChange, SpaceTable, SpaceTemplate } from "@/shared/types";

const ACCESS: { value: SpaceAccess; words: string }[] = [
  { value: "per_customer", words: "Each customer sees only their own rows" },
  { value: "shared", words: "Every run sees every row" },
];
const AGENT_CAN: { value: SpaceAgentCan; words: string }[] = [
  { value: "add_update", words: "Add and change rows" },
  { value: "add", words: "Add rows" },
  { value: "update", words: "Change rows" },
  { value: "read", words: "Only read" },
];

function SpaceDialog({ title, description, children, steps, action, danger = false, busy, ready, onAct, onClose, side }: {
  title: string;
  description: string;
  children?: ReactNode;
  steps: ReturnType<typeof usePlannedChange>;
  action: string;
  /** The action deletes: the solid danger button. */
  danger?: boolean;
  busy: boolean;
  ready: boolean;
  onAct: () => void;
  onClose: () => void;
  /** A second action, on the footer's left (Delete table). */
  side?: ReactNode;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[480px] p-0 gap-0 rounded-[16px] bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b hairline">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{title}</DialogTitle>
          <DialogDescription className="text-[12.5px] text-[var(--text-tertiary)] mt-1">{description}</DialogDescription>
        </div>
        {children && <div className="px-5 py-4 space-y-3">{children}</div>}
        <div className="border-t hairline max-h-[240px] overflow-y-auto">
          <SpacePlanSteps
            plan={steps.plan.data}
            pending={steps.waiting || steps.plan.isFetching}
            error={steps.plan.error ? steps.plan.error.message : null}
            className="px-2"
          />
        </div>
        <div className="px-5 py-3.5 border-t hairline flex items-center gap-2">
          {side}
          <span className="flex-1" />
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          {danger ? (
            <button type="button" onClick={onAct} disabled={!ready || busy} className={confirmDangerCls}>{action}</button>
          ) : (
            <BrandButton onClick={onAct} disabled={!ready || busy} className="disabled:opacity-40 disabled:pointer-events-none">
              {action}
            </BrandButton>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block mb-1.5 text-[12px] text-[var(--text-tertiary)]">{label}</span>
      {children}
    </label>
  );
}

export function NewTableDialog({ agentId, onMade, onClose }: {
  agentId: string;
  onMade: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [first, setFirst] = useState("Name");
  const [access, setAccess] = useState<SpaceAccess>("per_customer");
  const changes = useMemo<SpaceChange[] | null>(
    () => (name.trim() && first.trim()
      ? [{ op: "add_table", name: name.trim(), access, columns: [{ name: first.trim() }] }]
      : null),
    [name, first, access],
  );
  const steps = usePlannedChange(agentId, changes);
  return (
    <SpaceDialog
      title="New table"
      description="A table the agent keeps. Add columns and their rules once it's made."
      steps={steps}
      action="Create"
      busy={steps.pending}
      ready={steps.ready}
      onAct={() => steps.make(() => onMade(name.trim()))}
      onClose={onClose}
    >
      <Labelled label="Name">
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Leads" className={inputCls} />
      </Labelled>
      <Labelled label="Its first column names each row">
        <input value={first} onChange={(e) => setFirst(e.target.value)} className={inputCls} />
      </Labelled>
      <Labelled label="Who sees which rows">
        <select value={access} onChange={(e) => setAccess(e.target.value as SpaceAccess)} className={inputCls}>
          {ACCESS.map((entry) => <option key={entry.value} value={entry.value}>{entry.words}</option>)}
        </select>
      </Labelled>
    </SpaceDialog>
  );
}

export function TableSettingsDialog({ agentId, table, onDropped, onClose }: {
  agentId: string;
  table: SpaceTable;
  onDropped: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(table.name);
  const [note, setNote] = useState(table.note);
  const [access, setAccess] = useState(table.access);
  const [agentCan, setAgentCan] = useState(table.agent_can);
  const [notify, setNotify] = useState(table.notify_on_new_row);
  const [dropping, setDropping] = useState(false);
  const changes = useMemo<SpaceChange[] | null>(() => {
    if (dropping) return [{ op: "drop_table", table: table.id }];
    const fields: Record<string, unknown> = {};
    if (name.trim() && name.trim() !== table.name) fields.name = name.trim();
    if (note.trim() !== table.note) fields.note = note.trim();
    if (access !== table.access) fields.access = access;
    if (agentCan !== table.agent_can) fields.agent_can = agentCan;
    if (notify !== table.notify_on_new_row) fields.notify_on_new_row = notify;
    return Object.keys(fields).length ? [{ op: "edit_table", table: table.id, ...fields }] : null;
  }, [dropping, name, note, access, agentCan, notify, table]);
  const steps = usePlannedChange(agentId, changes);
  const save = () =>
    steps.make(() => {
      if (dropping) onDropped();
      onClose();
    });
  return (
    <SpaceDialog
      title={dropping ? `Delete ${table.name}?` : `${table.name} settings`}
      description={dropping ? "Its rows go with it. The plan says how many." : "What the agent knows of this table, whose rows it sees and what it may do."}
      steps={steps}
      action={dropping ? "Delete table" : "Save"}
      danger={dropping}
      busy={steps.pending}
      ready={steps.ready}
      onAct={save}
      onClose={onClose}
      side={dropping
        ? <GhostButton onClick={() => setDropping(false)}>Keep table</GhostButton>
        : <DangerButton onClick={() => setDropping(true)}>Delete table</DangerButton>}
    >
      {!dropping && (
        <>
          <Labelled label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </Labelled>
          <Labelled label="For the agent">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What the table is for and when to add a row" className={`${inputCls} h-auto py-2 resize-none`} />
          </Labelled>
          <div className="grid grid-cols-2 gap-3">
            <Labelled label="Who sees which rows">
              <select value={access} onChange={(e) => setAccess(e.target.value as SpaceAccess)} className={inputCls}>
                {ACCESS.map((entry) => <option key={entry.value} value={entry.value}>{entry.words}</option>)}
              </select>
            </Labelled>
            <Labelled label="The agent may">
              <select value={agentCan} onChange={(e) => setAgentCan(e.target.value as SpaceAgentCan)} className={inputCls}>
                {AGENT_CAN.map((entry) => <option key={entry.value} value={entry.value}>{entry.words}</option>)}
              </select>
            </Labelled>
          </div>
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <span>
              <span className="block text-[13px] font-medium text-[var(--text-primary)]">Tell me about new rows</span>
              <span className="block text-[12px] text-[var(--text-tertiary)]">In your bell and by email, when the agent adds one.</span>
            </span>
            <Switch checked={notify} onCheckedChange={setNotify} aria-label="Tell me about new rows" />
          </label>
        </>
      )}
    </SpaceDialog>
  );
}

export function TemplateDialog({ agentId, template, onMade, onClose }: {
  agentId: string;
  template: SpaceTemplate;
  onMade: () => void;
  onClose: () => void;
}) {
  const changes = useMemo<SpaceChange[]>(() => [{ op: "apply_template", template: template.id }], [template.id]);
  const steps = usePlannedChange(agentId, changes);
  return (
    <SpaceDialog
      title={template.name}
      description={template.description}
      steps={steps}
      action="Add these tables"
      busy={steps.pending}
      ready={steps.ready}
      onAct={() =>
        steps.make(() => {
          onMade();
          onClose();
        })
      }
      onClose={onClose}
    />
  );
}
